/**
 * Push Notification Service
 *
 * Manages FCM token lifecycle and notification permissions.
 *
 * Token flow:
 * 1. User logs in → requestPermission() prompts browser permission
 * 2. If granted → getToken() fetches FCM token from Firebase
 * 3. Token stored on user document in Firestore (fcmTokens array)
 * 4. Cloud Functions read tokens to send targeted notifications
 * 5. On token refresh → old token replaced automatically
 *
 * Notification types:
 * - sos: SOS alert from field tech → office/ops
 * - flag: Job flagged by tech → office/ops
 * - time_request: Tech requesting more time → office/ops
 * - job_change: Job rescheduled within 48hrs → assigned tech
 * - tech_consult: Fault consult request → assigned tech
 * - voting: Employee of the month voting opened → all staff
 */

import { getToken, onMessage } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, messagingPromise, firebaseConfig } from '@/firebase';

// VAPID key — get this from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
// This is a public key, safe to include in client code
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

/**
 * Check if push notifications are supported in this browser.
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get the current notification permission state.
 */
export function getPermissionState(): NotificationPermission {
  if (!isPushSupported()) return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission and register the FCM token.
 *
 * @param userId - The current user's Firestore document ID
 * @returns The FCM token string, or null if permission denied/unsupported
 */
export async function requestAndRegisterToken(userId: string): Promise<string | null> {
  if (!isPushSupported()) {
    console.warn('[Push] Not supported in this browser');
    return null;
  }

  // Request browser permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.log('[Push] Permission denied by user');
    return null;
  }

  const messaging = await messagingPromise;
  if (!messaging) {
    console.warn('[Push] Firebase Messaging not available');
    return null;
  }

  // Pass Firebase config to service worker via message
  const registration = await navigator.serviceWorker.getRegistration();
  if (registration?.active) {
    registration.active.postMessage({
      type: 'FIREBASE_CONFIG',
      config: firebaseConfig,
    });
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('[Push] FCM token obtained');
      await saveTokenToUser(userId, token);
      return token;
    }
    console.warn('[Push] No token returned');
    return null;
  } catch (err) {
    console.error('[Push] Token request failed:', err);
    return null;
  }
}

/**
 * Wait for Firebase Auth to have a current user. Resolves immediately if
 * auth is already established, otherwise waits for the first non-null
 * `onAuthStateChanged` event.
 *
 * Prevents races where Firestore writes fire before `signInWithCustomToken`
 * has settled — under tightened Firestore rules those writes would be denied.
 *
 * Returns `true` if auth settled within `timeoutMs`, `false` otherwise.
 */
function waitForAuthReady(timeoutMs = 10000): Promise<boolean> {
  if (auth.currentUser) return Promise.resolve(true);
  return new Promise((resolve) => {
    let resolved = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !resolved) {
        resolved = true;
        unsubscribe();
        resolve(true);
      }
    });
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsubscribe();
        resolve(false);
      }
    }, timeoutMs);
  });
}

/**
 * Save FCM token to the user's Firestore document.
 * Uses arrayUnion to support multiple devices per user.
 *
 * Waits for Firebase Auth before writing — otherwise the write may fire
 * before `signInWithCustomToken` has settled and Firestore will deny it
 * under the auth-required rules.
 */
async function saveTokenToUser(userId: string, token: string): Promise<void> {
  const ready = await waitForAuthReady();
  if (!ready) {
    console.warn('[Push] Auth never settled; skipping token save');
    return;
  }
  try {
    await updateDoc(doc(db, 'users', userId), {
      fcmTokens: arrayUnion(token),
      fcmTokenUpdatedAt: new Date().toISOString(),
    });
    console.log('[Push] Token saved to user document');
  } catch (err) {
    console.error('[Push] Failed to save token:', err);
  }
}

/**
 * Remove an FCM token from the user's document (e.g., on logout).
 *
 * Same auth-ready gate as `saveTokenToUser` — typically called during
 * logout, but the gate is cheap and makes the failure mode visible.
 */
export async function removeToken(userId: string, token: string): Promise<void> {
  const ready = await waitForAuthReady();
  if (!ready) {
    console.warn('[Push] Auth never settled; skipping token removal');
    return;
  }
  try {
    await updateDoc(doc(db, 'users', userId), {
      fcmTokens: arrayRemove(token),
    });
    console.log('[Push] Token removed from user document');
  } catch (err) {
    console.error('[Push] Failed to remove token:', err);
  }
}

/**
 * Listen for foreground FCM messages and show an in-app notification.
 * Returns an unsubscribe function.
 *
 * Foreground messages do NOT trigger the service worker's onBackgroundMessage.
 * We must handle them explicitly in the app.
 */
export async function listenForForegroundMessages(
  onNotification: (payload: {
    title: string;
    body: string;
    type: string;
    url: string;
    data: Record<string, string>;
  }) => void
): Promise<(() => void) | null> {
  const messaging = await messagingPromise;
  if (!messaging) return null;

  const unsubscribe = onMessage(messaging, (payload) => {
    console.log('[Push] Foreground message:', payload);

    const data = payload.data || {};
    const notification = payload.notification || {};

    // Resolve URL — if on a /tech route, remap /home fallback to /tech
    const rawUrl = data.url || '/home';
    const isTechUser = window.location.pathname.startsWith('/tech');
    const resolvedUrl = (isTechUser && rawUrl === '/home') ? '/tech' : rawUrl;

    onNotification({
      // TODO(saas-readiness): 'THEO' brand fallback — should be the tenant's
      // app name (tenant.companyName / systemName). This is a service with no
      // useTenant; payload.title is set by the sender in practice, so this is
      // a rarely-hit deep fallback. Thread tenant or read systemName here.
      title: data.title || notification.title || 'THEO',
      body: data.body || notification.body || '',
      type: data.type || 'general',
      url: resolvedUrl,
      data: data as Record<string, string>,
    });
  });

  return unsubscribe;
}
